"use client";

import React, { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { FaAddressCard, FaExclamationCircle } from "react-icons/fa";
import { useDialog } from "@/hooks/useDialog";
import { useForm } from "react-hook-form";
import Link from "next/link";
import apiAuth from "@/api/Auth";
import AltchaGate from "@/components/Altcha/AltchaGate";

export default function ForgotPasswordClient({
  recoveryEnabled = true,
  disabledMessage = "",
}) {
  const router = useRouter();
  const altchaRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const { show } = useDialog();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { cedula: "" },
    mode: "onSubmit",
  });

  const resetAltcha = () => {
    setCaptchaToken(null);
    altchaRef.current?.reset?.();
  };

  const onSubmit = async ({ cedula }) => {
    try {
      setIsLoading(true);

      const cedulaLimpia = String(cedula).trim();

      const res = await apiAuth.forgot(cedulaLimpia, captchaToken);

      if (res?.data?.success) {
        show({
          type: "success",
          title: "Solicitud enviada",
          message: `Te enviamos un enlace de restablecimiento al correo ${res.data.data.correo}. Revisa tu bandeja de entrada y también “Spam”.`,
          onConfirmation: () => router.push("/"),
        });
        return;
      }

      show({
        type: "error",
        title: "No se pudo enviar la solicitud",
        message: "Por favor, intenta nuevamente.",
      });
    } catch (error) {
      const status = error?.response?.status;
      const payload = error?.response?.data;

      if (status === 409 && payload?.data?.action?.url) {
        show({
          type: "warning",
          title: payload.error || "Atención",
          message: payload.message,
          onConfirmation: () => {
            window.location.href = payload.data.action.url;
          },
        });
        return;
      }

      if (error.response?.data) {
        show({
          type: "warning",
          title: error.response.data.error || "Atención",
          message: error.response.data.message || "Ocurrió un error.",
        });
      } else {
        const { name, message } = error;
        show({ type: "error", title: name, message });
      }
    } finally {
      resetAltcha();
      setIsLoading(false);
    }
  };

  if (!recoveryEnabled) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 dark:bg-gray-900">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <div className="p-6 sm:p-8">
            <h1 className="text-center text-2xl font-semibold text-slate-900 dark:text-white">
              Recuperar contraseña
            </h1>

            <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
              <p className="flex items-start gap-2 text-sm leading-6">
                <FaExclamationCircle className="mt-1 shrink-0" />
                <span>{disabledMessage}</span>
              </p>
            </div>

            <div className="mt-6 text-center">
              <Link
                href="/"
                className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:opacity-95"
              >
                Volver a iniciar sesión
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 dark:bg-gray-900">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
        <div className="p-6 sm:p-8">
          <h1 className="text-center text-2xl font-semibold text-slate-900 dark:text-white">
            Recuperar contraseña
          </h1>

          <p className="mt-2 text-center text-sm text-slate-600 dark:text-gray-300">
            Ingresa tu cédula y completa la validación para enviarte un enlace de
            restablecimiento.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-5">
            <div>
              <label
                htmlFor="cedula"
                className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-200"
              >
                Cédula / RUC
              </label>

              <div className="relative">
                <input
                  id="cedula"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="Ej: 1301234567"
                  className={[
                    "w-full rounded-lg border bg-slate-50 px-4 py-3 pr-12 text-slate-900 outline-none transition",
                    "dark:bg-gray-700 dark:text-white",
                    errors.cedula
                      ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-300/20"
                      : "border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:focus:ring-blue-300/20",
                  ].join(" ")}
                  {...register("cedula", {
                    required: "La cédula/RUC es obligatoria.",
                  })}
                />

                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-300">
                  <FaAddressCard size={18} />
                </span>
              </div>

              {errors.cedula && (
                <p className="mt-2 flex items-center gap-2 text-sm text-red-500">
                  <FaExclamationCircle />
                  {errors.cedula.message}
                </p>
              )}
            </div>

            <AltchaGate
              ref={altchaRef}
              className="flex justify-center"
              onToken={setCaptchaToken}
            />

            <button
              type="submit"
              disabled={isLoading || !captchaToken}
              className={[
                "flex w-full items-center justify-center rounded-lg px-4 py-3 font-semibold text-white shadow-md transition",
                "bg-primary hover:opacity-95",
                "disabled:cursor-not-allowed disabled:bg-slate-400 disabled:shadow-none",
              ].join(" ")}
            >
              {isLoading ? "Enviando solicitud..." : "Enviar enlace de recuperación"}
            </button>

            <div className="pt-1 text-center">
              <Link
                href="/"
                className="inline-flex items-center justify-center text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-gray-300 dark:hover:text-white"
              >
                Volver a iniciar sesión
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
